// src/config/sentry.js
// Sentry error tracking configuration

import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry error tracking
 * Only initializes in production or when DSN is provided
 */
export const initSentry = () => {
  const dsn = process.env.REACT_APP_SENTRY_DSN;

  // Skip initialization if no DSN provided or if it's a placeholder value
  if (!dsn || !dsn.startsWith("https://")) {
    if (process.env.NODE_ENV === "development") {
      console.log("Sentry: No valid DSN provided, skipping initialization");
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV || "development",

    // Performance monitoring sample rate (0.0 to 1.0)
    // Set to 0.1 (10%) in production to reduce costs
    tracesSampleRate: process.env.NODE_ENV === "production" ? 0.1 : 1.0,

    // Session replay for debugging (optional, disabled by default)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: process.env.NODE_ENV === "production" ? 0.1 : 0,

    // Only send errors in production, or all in development
    beforeSend(event) {
      // Filter out non-critical errors in production if needed
      if (process.env.NODE_ENV === "development") {
        console.log("Sentry event:", event);
      }
      return event;
    },

    // Ignore common browser extension errors
    ignoreErrors: [
      // Browser extensions
      "top.GLOBALS",
      "originalCreateNotification",
      "canvas.contentDocument",
      "MyApp_RemoveAllHighlights",
      "http://tt.teletracker.co",
      "jigsaw is not defined",
      "ComboSearch is not defined",
      "atomicFindClose",
      // Facebook errors
      "fb_xd_fragment",
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Network errors that aren't actionable
      "Network request failed",
      "Failed to fetch",
      "Load failed",
    ],
  });

  if (process.env.NODE_ENV === "development") {
    console.log("Sentry initialized successfully");
  }
};

/**
 * Capture an exception with optional context
 * @param {Error} error - The error to capture
 * @param {Object} context - Additional context (tags, extra data)
 */
export const captureException = (error, context = {}) => {
  if (!process.env.REACT_APP_SENTRY_DSN) {
    console.error("Error (Sentry not configured):", error);
    return;
  }

  Sentry.withScope((scope) => {
    // Add any tags
    if (context.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    // Add extra context
    if (context.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }

    // Set user context if available
    if (context.user) {
      scope.setUser(context.user);
    }

    // Set the error level
    if (context.level) {
      scope.setLevel(context.level);
    }

    Sentry.captureException(error);
  });
};

/**
 * Capture a message (non-error event)
 * @param {string} message - The message to capture
 * @param {string} level - Severity level (info, warning, error)
 */
export const captureMessage = (message, level = "info") => {
  if (!process.env.REACT_APP_SENTRY_DSN) {
    console.log(`Message (Sentry not configured) [${level}]:`, message);
    return;
  }

  Sentry.captureMessage(message, level);
};

/**
 * Set user context for error tracking
 * Call this after user login
 * @param {Object} user - User object with id, email, etc.
 */
export const setUser = (user) => {
  if (!process.env.REACT_APP_SENTRY_DSN) return;

  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.name || user.email,
      // Add user type (client, garage, admin)
      userType: user.role || user.user_type,
    });
  } else {
    // Clear user on logout
    Sentry.setUser(null);
  }
};

/**
 * Add breadcrumb for debugging
 * @param {Object} breadcrumb - Breadcrumb data
 */
export const addBreadcrumb = (breadcrumb) => {
  if (!process.env.REACT_APP_SENTRY_DSN) return;

  Sentry.addBreadcrumb({
    category: breadcrumb.category || "action",
    message: breadcrumb.message,
    level: breadcrumb.level || "info",
    data: breadcrumb.data,
  });
};

export default {
  initSentry,
  captureException,
  captureMessage,
  setUser,
  addBreadcrumb,
};
